import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Pencil, Trash2, Search, X, Mail, Phone as PhoneIcon, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { PageTitle } from "./Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";

const EMPTY = { name: "", email: "", phone: "", note: "" };

function Field({ label, children, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-stone-600 mb-1">{label}</span>
      <div data-testid={testid}>{children}</div>
    </label>
  );
}

function ContactDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      note: form.note.trim(),
    };
    if (!payload.name && !payload.email && !payload.phone) {
      toast.error("Ad, e-posta veya telefon alanlarından en az biri dolu olmalı");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/contacts/${initial.id}`, payload);
      else await api.post("/admin/contacts", payload);
      toast.success(isEdit ? "Kişi güncellendi" : "Kişi eklendi");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="contact-dialog" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 h-14 border-b">
          <h3 className="font-serif text-xl font-semibold">{isEdit ? "Kişiyi düzenle" : "Yeni kişi"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="contact-dialog-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Ad soyad" testid="contact-field-name">
            <Input value={form.name} onChange={set("name")} placeholder="Ör. Ayşe Yılmaz" data-testid="contact-input-name" />
          </Field>
          <Field label="E-posta" testid="contact-field-email">
            <Input type="email" value={form.email} onChange={set("email")} placeholder="ornek@eposta.com" data-testid="contact-input-email" />
          </Field>
          <Field label="Telefon" testid="contact-field-phone">
            <Input value={form.phone} onChange={set("phone")} placeholder="+90 555 123 45 67" data-testid="contact-input-phone" />
          </Field>
          <Field label="Not" testid="contact-field-note">
            <Textarea value={form.note} onChange={set("note")} rows={3} placeholder="Nereden tanışıldı, konu, vb." data-testid="contact-input-note" />
          </Field>
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="contact-cancel">İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="contact-save">{saving ? "Kaydediliyor…" : (isEdit ? "Güncelle" : "Ekle")}</Button>
        </div>
      </div>
    </div>
  );
}

function sourceLabel(src) {
  switch (src) {
    case "telegram": return "Telegram";
    case "admin": return "Admin";
    case "manual": return "Manuel";
    default: return src || "—";
  }
}

export default function ContactsAdmin() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | {} new | contact edit
  const { data = [], isLoading } = useQuery({
    queryKey: ["admin", "contacts", q],
    queryFn: () => api.get(`/admin/contacts${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => r.data),
    keepPreviousData: true,
  });

  const total = data.length;
  const sources = useMemo(() => {
    const m = {};
    for (const c of data) m[c.source || "manual"] = (m[c.source || "manual"] || 0) + 1;
    return m;
  }, [data]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "contacts"] });

  const del = async (c) => {
    if (!window.confirm(`"${c.name || c.email || c.phone}" silinsin mi?`)) return;
    try { await api.delete(`/admin/contacts/${c.id}`); refresh(); toast.success("Kişi silindi"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageTitle
        title="Rehber"
        sub="Telegram randevu akışından ve manuel olarak eklenen kişiler."
        action={
          <Button onClick={() => setEditing({})} data-testid="contact-new">
            <Plus size={16} className="mr-1" /> Yeni kişi
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ad, e-posta, telefon veya nota göre ara…"
            className="pl-9"
            data-testid="contacts-search"
          />
        </div>
        <div className="text-xs text-stone-500 flex items-center gap-2" data-testid="contacts-stats">
          <Users size={14} /> {total} kişi
          {Object.keys(sources).length > 0 && (
            <span className="text-stone-300">·</span>
          )}
          {Object.entries(sources).map(([k, v]) => (
            <Badge key={k} variant="secondary" className="font-normal">{sourceLabel(k)}: {v}</Badge>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-xl divide-y" data-testid="contacts-list">
        {isLoading && <p className="p-6 text-sm text-stone-400">Yükleniyor…</p>}
        {!isLoading && !data.length && (
          <p className="p-6 text-sm text-stone-400">Kayıtlı kişi yok. Sağ üstten yeni ekleyebilir ya da Telegram'dan randevu oluşturarak otomatik kayıt yapabilirsiniz.</p>
        )}
        {data.map((c) => (
          <div key={c.id} className="px-5 py-3.5 flex items-start gap-4 hover:bg-stone-50" data-testid={`contact-row-${c.id}`}>
            <div className="w-10 h-10 rounded-full bg-stone-100 text-stone-500 flex items-center justify-center shrink-0 font-semibold">
              {(c.name || c.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold truncate">{c.name || <span className="text-stone-400 font-normal">İsimsiz</span>}</p>
                <Badge variant="secondary" className="font-normal text-[10px]">{sourceLabel(c.source)}</Badge>
              </div>
              <div className="text-sm text-stone-600 flex items-center gap-4 flex-wrap mt-0.5">
                {c.email && <span className="flex items-center gap-1"><Mail size={13} /> {c.email}</span>}
                {c.phone && <span className="flex items-center gap-1"><PhoneIcon size={13} /> {c.phone}</span>}
              </div>
              {c.note && (
                <p className="text-sm text-stone-500 flex items-start gap-1 mt-1">
                  <StickyNote size={13} className="mt-0.5 shrink-0" />
                  <span className="line-clamp-2">{c.note}</span>
                </p>
              )}
              <p className="text-[11px] text-stone-400 mt-1">
                Son kullanım: {c.last_used_at ? new Date(c.last_used_at).toLocaleString("tr-TR") : "—"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setEditing(c)} className="p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-md" data-testid={`contact-edit-${c.id}`} title="Düzenle">
                <Pencil size={15} />
              </button>
              <button onClick={() => del(c)} className="p-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-md" data-testid={`contact-delete-${c.id}`} title="Sil">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <ContactDialog initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={refresh} />
      )}
    </>
  );
}
